import { API_BASE_URL } from "@/lib/api/config";

const MEDIA_BACKEND_ORIGIN = new URL(API_BASE_URL).origin;

const getCanonicalPostMediaPath = (value: string): string | null => {
  const path = value.replace(/^\/+/, "");
  const postKey = ["static/uploads/", "uploads/", "storage/", "api/v1/storage/file/"]
    .reduce<string | null>((resolved, prefix) => {
      if (resolved || !path.startsWith(prefix)) return resolved;
      const candidate = path.slice(prefix.length);
      return candidate.startsWith("posts/") ? candidate : null;
    }, path.startsWith("posts/") ? path : null);

  return postKey ? `static/uploads/${postKey}` : null;
};

export const getMediaUrl = (pathOrKey: string | null | undefined): string | null => {
  if (!pathOrKey) return null;
  if (pathOrKey.startsWith("blob:") || pathOrKey.startsWith("data:")) return pathOrKey;

  if (/^https?:\/\//i.test(pathOrKey)) {
    try {
      const url = new URL(pathOrKey);
      // Disa endpoints kthejnë storage/storage ose static/uploads për të
      // njëjtin objekt; publikojmë vetëm canonical /storage/{key}.
      // Keep /static/uploads for profile and post assets; only collapse a
      // duplicated /storage/storage prefix for MediMarket assets.
      url.pathname = url.pathname
        .replace(/^\/(?:storage\/)+(?=storage\/)/, "/storage/")
        .replace(/^\/storage\/storage\//, "/storage/");
      const canonicalPostPath = getCanonicalPostMediaPath(url.pathname);
      if (canonicalPostPath) url.pathname = `/${canonicalPostPath}`;
      if (url.pathname.startsWith("/storage/products/")) {
        url.pathname = `/static/uploads/${url.pathname.slice("/storage/".length)}`;
      }
      if (url.pathname.startsWith("/storage/profiles/")) {
        url.pathname = `/static/uploads/${url.pathname.slice("/storage/".length)}`;
      }
      return url.toString();
    } catch {
      return pathOrKey;
    }
  }

  if (pathOrKey.startsWith("/")) {
    pathOrKey = pathOrKey.replace(/^\/+/, "");
  }

  const canonicalPostPath = getCanonicalPostMediaPath(pathOrKey);
  if (canonicalPostPath) {
    return `${MEDIA_BACKEND_ORIGIN}/${canonicalPostPath}`;
  }

  if (pathOrKey.startsWith("storage/storage/")) {
    return `${MEDIA_BACKEND_ORIGIN}/storage/${pathOrKey.slice("storage/storage/".length)}`;
  }

  // Product uploads use the local storage provider's canonical public path.
  // The hosting SPA may answer /storage/products/* with index.html (200),
  // which still leaves an <img> visually broken.
  if (pathOrKey.startsWith("storage/products/")) {
    return `${MEDIA_BACKEND_ORIGIN}/static/uploads/${pathOrKey.slice("storage/".length)}`;
  }

  // Profile avatars are public uploads. On the hosted frontend the legacy
  // /storage/profiles path is not served, while /static/uploads/profiles is.
  if (pathOrKey.startsWith("storage/profiles/")) {
    return `${MEDIA_BACKEND_ORIGIN}/static/uploads/${pathOrKey.slice("storage/".length)}`;
  }

  if (pathOrKey.startsWith("storage/")) {
    return `${MEDIA_BACKEND_ORIGIN}/${pathOrKey}`;
  }

  if (pathOrKey.startsWith("static/uploads/")) {
    return `${MEDIA_BACKEND_ORIGIN}/${pathOrKey}`;
  }

  if (pathOrKey.startsWith("uploads/")) {
    return `${MEDIA_BACKEND_ORIGIN}/storage/${pathOrKey.slice("uploads/".length)}`;
  }

  const cleanPath = pathOrKey.replace(/^\/+/, "");

  if (cleanPath.startsWith("static/uploads.storage/")) {
    return `${MEDIA_BACKEND_ORIGIN}/storage/${cleanPath.slice("static/uploads.storage/".length)}`;
  }

  if (cleanPath.startsWith("uploads.storage/")) {
    return `${MEDIA_BACKEND_ORIGIN}/storage/${cleanPath.slice("uploads.storage/".length)}`;
  }

  if (cleanPath.startsWith("static/")) {
    return `${MEDIA_BACKEND_ORIGIN}/${cleanPath}`;
  }

  if (cleanPath.startsWith("uploads/")) {
    return `${MEDIA_BACKEND_ORIGIN}/static/${cleanPath}`;
  }

  return `${MEDIA_BACKEND_ORIGIN}/storage/${cleanPath}`;
};

export const resolveMediaUrl = getMediaUrl;
export const formatImageUrl = getMediaUrl;
